import Link from "next/link";
import { MouseEvent, useState } from "react";
import { toast } from "react-hot-toast";

import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useActiveWatchlist } from "@/features/watchlist/services/get-active-watchlist";
import { useUpdateActiveWatchlist } from "@/features/watchlist/services/update-active-watchlist";
import { useLang } from "@/hooks/use-lang";

export const MutateWatchlistWrapper = ({
  symbol,
  children,
  isInWatchlist,
  className,
  onMutateSymbol,
}: {
  symbol: string;
  children: JSX.Element;
  isInWatchlist: boolean;
  className?: string;
  onMutateSymbol?: (isMutating: boolean) => void;
}) => {
  const { t, lang } = useLang();
  const [isDisabled, setIsDisabled] = useState(false);
  const { isUnauthenticated } = useAuth();

  const { data: activeWatchlist } = useActiveWatchlist();
  const { mutate: updateActiveWatchlist } = useUpdateActiveWatchlist();

  if (isUnauthenticated)
    return (
      <Link
        href={`/${lang}${links.authSignup}`}
        className={className}
        aria-label="sign up to use watchlist"
      >
        {children}
      </Link>
    );

  const handleUpdate = () => {
    if (!activeWatchlist) return;
    onMutateSymbol?.(true);
    setIsDisabled(true);
    const newSymbols = isInWatchlist
      ? activeWatchlist.symbols.filter((s) => s !== symbol)
      : activeWatchlist.symbols.concat(symbol);

    updateActiveWatchlist(
      { symbols: newSymbols },
      {
        onSuccess: () => {
          toast.success(
            isInWatchlist
              ? t.layout.watchlist.removeSymbolSuccessToast(symbol)
              : t.layout.watchlist.addSymbolSuccessToast(symbol)
          );
        },
        onError: () => {
          toast.error(t.error.serverError);
        },
        onSettled: () => {
          onMutateSymbol?.(false);
          setIsDisabled(false);
        },
      }
    );
  };

  const handleOnClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (isDisabled) return;
    handleUpdate();
  };

  return (
    <span
      className={className}
      onClick={(e) => handleOnClick(e)}
      aria-label={
        isInWatchlist
          ? `remove ${symbol} from watchlist`
          : `add ${symbol} to watchlist`
      }
    >
      {children}
    </span>
  );
};
